import React, { useState, useEffect } from 'react';
import { FileText, Plus, Edit2, Trash2, Search, Filter, Loader2, ChevronLeft, ChevronRight, Copy, Eye, XCircle, CheckCircle, Grid, List } from 'lucide-react';
import { communicationService } from '../../../services/communicationService';
import TemplateModal from './TemplateModal';

const TemplateManagement = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterMessageType, setFilterMessageType] = useState('all');
  const [filterActive, setFilterActive] = useState('all'); // 'all', 'active', 'inactive'
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50); // Increased default for better scalability
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState('table'); // Default to table view for scalability

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1); // Reset to first page when search changes
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load templates when filters or pagination changes
  useEffect(() => {
    loadTemplates();
  }, [currentPage, pageSize, filterType, filterMessageType, filterActive, debouncedSearchQuery]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const filters = { 
        page: currentPage,
        pageSize: pageSize
      };

      // Apply filter type (static/templated/runtime)
      if (filterType === 'static') {
        filters.isStatic = true;
      } else if (filterType === 'templated') {
        filters.isStatic = false;
        filters.isRuntime = false;
      } else if (filterType === 'runtime') {
        filters.isRuntime = true;
      }

      // Apply message type filter
      if (filterMessageType !== 'all') {
        filters.messageType = filterMessageType;
      }

      // Apply active/inactive filter
      if (filterActive === 'active') {
        filters.isActive = true;
      } else if (filterActive === 'inactive') {
        filters.isActive = false;
      }

      // Apply search filter
      if (debouncedSearchQuery.trim().length > 0) {
        filters.search = debouncedSearchQuery;
      }

      const result = await communicationService.getAllTemplates(filters);
      setTemplates(result.data || []);
      setTotalCount(result.total || 0);
      setTotalPages(Math.ceil((result.total || 0) / pageSize));
    } catch (error) {
      console.error('Error loading templates:', error);
      alert('Failed to load templates: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleFilterChange = (filterName, value) => {
    if (filterName === 'type') {
      setFilterType(value);
    } else if (filterName === 'messageType') {
      setFilterMessageType(value);
    } else if (filterName === 'active') {
      setFilterActive(value);
    }
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const handleViewTemplate = (template) => {
    setViewingTemplate(template);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template? This action cannot be undone.')) return;

    try {
      await communicationService.deleteTemplate(templateId);
      await loadTemplates();
      alert('Template deleted successfully');
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template: ' + error.message);
    }
  };

  const handleCopyTemplate = async (template) => {
    try {
      const templateData = {
        templateId: template.template_id || '',
        name: `${template.name} (Copy)`,
        templateMessage: template.template_message,
        isStaticMessage: template.is_static_message,
        isRuntimeMessage: template.is_runtime_message,
        messageType: template.message_type,
        triggerApplicableType: template.trigger_applicable_type,
        templateType: template.template_type,
        provider: template.provider,
        language: template.language,
        category: template.category,
        isActive: false, // Set copied template as inactive by default
        templatedVars: template.templated_vars || [],
        approvalStatus: 'PENDING'
      };
      await communicationService.createTemplate(templateData);
      await loadTemplates();
      alert('Template copied successfully');
    } catch (error) {
      console.error('Error copying template:', error);
      alert('Failed to copy template: ' + error.message);
    }
  };

  const handleSaveTemplate = async (templateData, templateId) => {
    if (templateId) {
      // Update existing
      const updates = {
        template_id: templateData.templateId,
        name: templateData.name,
        template_message: templateData.isRuntimeMessage ? null : templateData.templateMessage,
        is_static_message: templateData.isStaticMessage,
        is_runtime_message: templateData.isRuntimeMessage,
        message_type: templateData.messageType,
        trigger_applicable_type: templateData.triggerApplicableType,
        template_type: templateData.templateType,
        provider: templateData.provider,
        language: templateData.language,
        category: templateData.category,
        is_active: templateData.isActive,
        templated_vars: templateData.templatedVars
      };
      await communicationService.updateTemplate(templateId, updates);
    } else {
      // Create new
      await communicationService.createTemplate(templateData);
    }
    await loadTemplates();
    setIsModalOpen(false);
    setEditingTemplate(null);
  };

  const toggleTemplateActive = async (template) => {
    try {
      await communicationService.updateTemplate(template.id, {
        is_active: !template.is_active
      });
      await loadTemplates();
    } catch (error) {
      console.error('Error toggling template status:', error);
      alert('Failed to update template status: ' + error.message);
    }
  };

  return (
    <>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Message Templates</h2>
                {totalCount > 0 && (
                  <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs sm:text-sm font-medium">
                    {totalCount.toLocaleString()} {totalCount === 1 ? 'template' : 'templates'}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Manage your SMS and WhatsApp message templates
                {viewMode === 'table' && ' • Table view optimized for large datasets'}
              </p>
            </div>
            <button
              onClick={handleCreateTemplate}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base font-medium"
            >
              <Plus className="w-4 h-4" />
              <span>Create Template</span>
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search templates by name or type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                  title="Clear search"
                >
                  <XCircle className="w-4 h-4 text-gray-400" />
                </button>
              )}
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Filters:</span>
              </div>
              
              {/* Template Type Filter */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleFilterChange('type', 'all')}
                  className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    filterType === 'all'
                      ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Types
                </button>
                <button
                  onClick={() => handleFilterChange('type', 'static')}
                  className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    filterType === 'static'
                      ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Static
                </button>
                <button
                  onClick={() => handleFilterChange('type', 'templated')}
                  className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    filterType === 'templated'
                      ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Templated
                </button>
                <button
                  onClick={() => handleFilterChange('type', 'runtime')}
                  className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    filterType === 'runtime'
                      ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Runtime
                </button>
              </div>

              {/* Message Type Filter */}
              <div className="flex flex-wrap gap-2">
                <select
                  value={filterMessageType}
                  onChange={(e) => handleFilterChange('messageType', e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="all">All Channels</option>
                  <option value="SMS">SMS</option>
                  <option value="WHATSAPP">WhatsApp</option>
                </select>
              </div>

              {/* Active/Inactive Filter */}
              <div className="flex flex-wrap gap-2">
                <select
                  value={filterActive}
                  onChange={(e) => handleFilterChange('active', e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center gap-2 ml-auto">
                <button
                  onClick={() => setViewMode('card')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'card' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Card view"
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'table' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Table view"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Templates Display */}
        {loading && templates.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading templates...</p>
          </div>
        ) : templates.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No templates found</h3>
            <p className="text-gray-600 mb-4">
              {debouncedSearchQuery.trim() 
                ? `No templates match your search "${debouncedSearchQuery}"`
                : 'Get started by creating your first message template'}
            </p>
            {!debouncedSearchQuery.trim() && (
              <button
                onClick={handleCreateTemplate}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Template
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Card View */}
            {viewMode === 'card' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map(template => (
                  <div
                    key={template.id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{template.name}</h3>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap mt-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            template.is_active 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {template.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                            {template.message_type}
                          </span>
                          {template.is_runtime_message && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                              Runtime
                            </span>
                          )}
                          {template.is_static_message && !template.is_runtime_message && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                              Static
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {template.template_message && (
                      <p className="text-xs sm:text-sm text-gray-600 line-clamp-3 mb-3">
                        {template.template_message}
                      </p>
                    )}

                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">{template.template_type}</span>
                        {template.provider && (
                          <>
                            <span className="text-xs text-gray-400">•</span>
                            <span className="text-xs text-gray-500">{template.provider}</span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleViewTemplate(template)}
                          className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                          title="View details"
                        >
                          <Eye className="w-4 h-4 text-gray-600" />
                        </button>
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="p-1.5 hover:bg-blue-100 rounded transition-colors"
                          title="Edit template"
                        >
                          <Edit2 className="w-4 h-4 text-blue-600" />
                        </button>
                        <button
                          onClick={() => handleCopyTemplate(template)}
                          className="p-1.5 hover:bg-purple-100 rounded transition-colors"
                          title="Copy template"
                        >
                          <Copy className="w-4 h-4 text-purple-600" />
                        </button>
                        <button
                          onClick={() => toggleTemplateActive(template)}
                          className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                          title={template.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {template.is_active ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="p-1.5 hover:bg-red-100 rounded transition-colors"
                          title="Delete template"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Table View - Optimized for large datasets */}
            {viewMode === 'table' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Desktop Table - Compact for scalability */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Template Name
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Channel
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Provider
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {templates.map(template => (
                        <tr key={template.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-2.5">
                            <div className="font-medium text-gray-900 text-sm truncate max-w-xs" title={template.name}>
                              {template.name}
                            </div>
                            {template.template_message && (
                              <div className="text-xs text-gray-500 mt-0.5 truncate max-w-xs" title={template.template_message}>
                                {template.template_message}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                                {template.template_type}
                              </span>
                              {template.is_runtime_message && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">
                                  R
                                </span>
                              )}
                              {template.is_static_message && !template.is_runtime_message && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                                  S
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                              {template.message_type}
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                              template.is_active 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {template.is_active ? '✓' : '✗'}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-xs text-gray-600 truncate max-w-[100px]" title={template.provider || 'N/A'}>
                            {template.provider || 'N/A'}
                          </td>
                          <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                            {new Date(template.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <div className="flex items-center justify-end gap-0.5">
                              <button
                                onClick={() => handleViewTemplate(template)}
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                                title="View details"
                              >
                                <Eye className="w-3.5 h-3.5 text-gray-600" />
                              </button>
                              <button
                                onClick={() => handleEditTemplate(template)}
                                className="p-1 hover:bg-blue-100 rounded transition-colors"
                                title="Edit template"
                              >
                                <Edit2 className="w-3.5 h-3.5 text-blue-600" />
                              </button>
                              <button
                                onClick={() => handleCopyTemplate(template)}
                                className="p-1 hover:bg-purple-100 rounded transition-colors"
                                title="Copy template"
                              >
                                <Copy className="w-3.5 h-3.5 text-purple-600" />
                              </button>
                              <button
                                onClick={() => toggleTemplateActive(template)}
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                                title={template.is_active ? 'Deactivate' : 'Activate'}
                              >
                                {template.is_active ? (
                                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                                ) : (
                                  <XCircle className="w-3.5 h-3.5 text-gray-400" />
                                )}
                              </button>
                              <button
                                onClick={() => handleDeleteTemplate(template.id)}
                                className="p-1 hover:bg-red-100 rounded transition-colors"
                                title="Delete template"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-600" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden divide-y divide-gray-200">
                  {templates.map(template => (
                    <div key={template.id} className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 text-sm">{template.name}</h3>
                          {template.template_message && (
                            <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                              {template.template_message}
                            </p>
                          )}
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium flex-shrink-0 ml-2 ${
                          template.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {template.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700">
                          {template.template_type}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                          {template.message_type}
                        </span>
                        {template.is_runtime_message && (
                          <span className="text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">
                            Runtime
                          </span>
                        )}
                        {template.is_static_message && !template.is_runtime_message && (
                          <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
                            Static
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          {new Date(template.created_at).toLocaleDateString()}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleViewTemplate(template)}
                            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                            title="View"
                          >
                            <Eye className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleEditTemplate(template)}
                            className="p-1.5 hover:bg-blue-100 rounded transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleCopyTemplate(template)}
                            className="p-1.5 hover:bg-purple-100 rounded transition-colors"
                            title="Copy"
                          >
                            <Copy className="w-4 h-4 text-purple-600" />
                          </button>
                          <button
                            onClick={() => toggleTemplateActive(template)}
                            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                            title={template.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {template.is_active ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-gray-400" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="p-1.5 hover:bg-red-100 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm text-gray-600">Show:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="px-2 py-1 border border-gray-300 rounded text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={500}>500</option>
                  </select>
                  <span className="text-xs sm:text-sm text-gray-600">per page</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm text-gray-600">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-1.5 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs sm:text-sm text-gray-700 px-2">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-1.5 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Template Modal */}
      <TemplateModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTemplate(null);
        }}
        template={editingTemplate}
        onSave={handleSaveTemplate}
      />

      {/* View Template Modal */}
      {viewingTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">{viewingTemplate.name}</h3>
                <button
                  onClick={() => setViewingTemplate(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XCircle className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs sm:text-sm text-gray-600">Status:</span>
                  <p className={`text-sm sm:text-base font-medium ${
                    viewingTemplate.is_active ? 'text-green-700' : 'text-gray-600'
                  }`}>
                    {viewingTemplate.is_active ? 'Active' : 'Inactive'}
                  </p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600">Message Type:</span>
                  <p className="text-sm sm:text-base font-medium text-gray-900">{viewingTemplate.message_type}</p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600">Template Type:</span>
                  <p className="text-sm sm:text-base font-medium text-gray-900">{viewingTemplate.template_type}</p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600">Provider:</span>
                  <p className="text-sm sm:text-base font-medium text-gray-900">{viewingTemplate.provider || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600">Category:</span>
                  <p className="text-sm sm:text-base font-medium text-gray-900">{viewingTemplate.category || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600">Language:</span>
                  <p className="text-sm sm:text-base font-medium text-gray-900">{viewingTemplate.language || 'en'}</p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600">Template ID:</span>
                  <p className="text-sm sm:text-base font-medium text-gray-900">{viewingTemplate.template_id || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600">Created:</span>
                  <p className="text-sm sm:text-base font-medium text-gray-900">
                    {new Date(viewingTemplate.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              {viewingTemplate.template_message && (
                <div>
                  <span className="text-xs sm:text-sm text-gray-600 block mb-2">Template Message:</span>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm sm:text-base text-gray-900 whitespace-pre-wrap">
                      {viewingTemplate.template_message}
                    </p>
                  </div>
                </div>
              )}
              {viewingTemplate.templated_vars && viewingTemplate.templated_vars.length > 0 && (
                <div>
                  <span className="text-xs sm:text-sm text-gray-600 block mb-2">Variables:</span>
                  <div className="flex flex-wrap gap-2">
                    {viewingTemplate.templated_vars.map((varName, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs sm:text-sm">
                        {`{{${varName}}}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setViewingTemplate(null);
                    handleEditTemplate(viewingTemplate);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
                >
                  Edit Template
                </button>
                <button
                  onClick={() => {
                    setViewingTemplate(null);
                    handleCopyTemplate(viewingTemplate);
                  }}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base"
                >
                  Copy Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TemplateManagement;

